import React, { useCallback, useEffect, useState } from "react";
import {
  Table,
  Tag,
  Button,
  Switch,
  Space,
  Popconfirm,
  notification,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import RuleBuilderDrawer from "./RuleBuilderDrawer";
import { fetchRules, setRuleStatus, deleteRule } from "./service";
import { RULE_STATUS_COLORS, APPROVAL_MODE_COLORS } from "./constants";
import styles from "./style.module.scss";
import { BrandTag } from "pages/SurveyManagement/phoneTriggerShared";

const EXAMPLE_RULES = [
  {
    trigger: "NPS score 0–6 detected",
    channel: "WhatsApp",
    template: "Detractor recovery message",
    approval: "Automatic",
  },
  {
    trigger: "VIP customer complaint flagged",
    channel: "AI Voice Call",
    template: "VIP retention script",
    approval: "1-click approval required",
  },
  {
    trigger: "Order delivered (webhook)",
    channel: "WhatsApp",
    template: "Post-delivery CSAT",
    approval: "Automatic",
  },
];

/** ``readOnly``: the Engage plan has ended; rules are listed, not created, edited or switched. */
const RulesTab = ({ active, onRulesChanged, setupMode = false, readOnly = false }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await fetchRules();
    if (res && !res.error) setRules(res.rules || []);
    else if (res && res.error) notification.error({ message: res.error });
    setLoading(false);
  }, []);

  useEffect(() => {
    if (active) load();
  }, [active, load]);

  useEffect(() => {
    if (!active) return;
    const params = new URLSearchParams(location.search);
    if (params.get("create") === "1" && !readOnly) {
      setEditing(null);
      setDrawerOpen(true);
    }
  }, [active, location.search, readOnly]);

  const openCreate = () => {
    setEditing(null);
    setDrawerOpen(true);
  };

  const openEdit = (rule) => {
    setEditing(rule);
    setDrawerOpen(true);
  };

  const handleToggle = async (rule, checked) => {
    if (rule.trigger_type === "voice" && checked) {
      notification.warning({ message: t("sonic.voice_not_activatable") });
      return;
    }
    setTogglingId(rule.id);
    const res = await setRuleStatus(rule.id, checked ? "active" : "paused");
    setTogglingId(null);
    if (res && !res.error) load();
    else notification.error({ message: (res && res.error) || t("sonic.action_failed") });
  };

  const handleDelete = async (rule) => {
    const res = await deleteRule(rule.id);
    if (res && !res.error) {
      notification.success({ message: t("sonic.rule_deleted") });
      load();
      if (onRulesChanged) onRulesChanged();
    } else {
      notification.error({ message: (res && res.error) || t("sonic.action_failed") });
    }
  };

  const handleSaved = () => {
    setDrawerOpen(false);
    load();
    if (onRulesChanged) onRulesChanged();
  };

  const columns = [
    {
      title: t("sonic.col_name"),
      dataIndex: "name",
      key: "name",
      render: (name) => <span className={styles.cellName}>{name}</span>,
    },
    {
      title: t("sonic.col_trigger"),
      dataIndex: "trigger_type",
      key: "trigger_type",
      render: (v) => <Tag>{v}</Tag>,
    },
    {
      title: t("sonic.col_channel"),
      dataIndex: "channel",
      key: "channel",
      render: (v) => <BrandTag tone="neutral">{v}</BrandTag>,
    },
    {
      title: t("sonic.col_template"),
      dataIndex: "template_name",
      key: "template_name",
      render: (v) => v || "—",
    },
    {
      title: t("sonic.col_approval"),
      dataIndex: "approval_mode",
      key: "approval_mode",
      render: (v) => <BrandTag tone={APPROVAL_MODE_COLORS[v]}>{v}</BrandTag>,
    },
    {
      title: t("sonic.col_status"),
      dataIndex: "status",
      key: "status",
      render: (v) => (
        <BrandTag tone={RULE_STATUS_COLORS[v]}>{v}</BrandTag>
      ),
    },
    !readOnly && {
      title: t("sonic.col_actions"),
      key: "actions",
      render: (_, rule) => (
        <Space>
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEdit(rule)}
          />
          <Switch
            size="small"
            checked={rule.status === "active"}
            loading={togglingId === rule.id}
            disabled={rule.trigger_type === "voice"}
            onChange={(checked) => handleToggle(rule, checked)}
          />
          <Popconfirm
            title={t("sonic.confirm_delete_rule")}
            onConfirm={() => handleDelete(rule)}
            okText={t("sonic.delete")}
            cancelText={t("sonic.cancel")}
          >
            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className={styles.tabInner}>
      <div className={styles.sonicPanel}>
      {rules.length === 0 && !loading ? (
        <div className={styles.emptyState}>
          <ThunderboltOutlined className={styles.emptyStateIcon} />
          <h3>
            {setupMode ? t("sonic.setup_step3_title") : t("sonic.rules_empty_title")}
          </h3>
          <p>
            {setupMode ? t("sonic.setup_step3_desc") : t("sonic.rules_empty_body")}
          </p>
          {readOnly ? null : (
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              {setupMode ? t("sonic.setup_step3_cta") : t("sonic.create_first_rule")}
            </Button>
          )}

          {!setupMode && (
          <div className={styles.exampleRules}>
            {EXAMPLE_RULES.map((ex) => (
              <div key={ex.trigger} className={styles.exampleRuleCard}>
                <div className={styles.exampleRuleRow}>
                  <span className={styles.exampleRuleLabel}>{t("sonic.example_trigger")}</span>
                  <span>{ex.trigger}</span>
                </div>
                <div className={styles.exampleRuleRow}>
                  <span className={styles.exampleRuleLabel}>{t("sonic.col_channel")}</span>
                  <span>{ex.channel}</span>
                </div>
                <div className={styles.exampleRuleRow}>
                  <span className={styles.exampleRuleLabel}>{t("sonic.col_template")}</span>
                  <span>{ex.template}</span>
                </div>
                <div className={styles.exampleRuleRow}>
                  <span className={styles.exampleRuleLabel}>{t("sonic.col_approval")}</span>
                  <span>{ex.approval}</span>
                </div>
              </div>
            ))}
          </div>
          )}
        </div>
      ) : (
        <>
          <div className={styles.sectionHeader}>
            <h3>{t("sonic.all_rules")}</h3>
            {readOnly ? null : (
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                {t("sonic.new_rule")}
              </Button>
            )}
          </div>

          <Table
            rowKey="id"
            loading={loading}
            columns={columns.filter(Boolean)}
            dataSource={rules}
            pagination={{ pageSize: 10, hideOnSinglePage: true }}
          />
        </>
      )}
      </div>

      <RuleBuilderDrawer
        open={drawerOpen}
        rule={editing}
        onClose={() => setDrawerOpen(false)}
        onSaved={handleSaved}
      />
    </div>
  );
};

export default RulesTab;
